import {
  extractApiError,
  ExtractedApiError,
  createCustomerFacingMessage,
  extractApiErrorFromResponse,
} from './extractApiError';
import type { ErrorWithHttpAttachments } from './httpErrorGuards';

/**
 * Enhanced error that includes all extracted API error information.
 * Implements {@link ErrorWithHttpAttachments} so handlers can read `response` / `body`.
 */
export class ApiError extends Error implements ErrorWithHttpAttachments {
  public readonly extractedError: ExtractedApiError;
  public readonly customerMessage: string;
  public readonly response?: unknown;
  public readonly body?: unknown;

  constructor(extractedError: ExtractedApiError, customerMessage?: string) {
    super(extractedError.error.message);
    this.name = 'ApiError';
    this.extractedError = extractedError;
    this.customerMessage =
      customerMessage || createCustomerFacingMessage(extractedError);

    if (extractedError.responseBody !== undefined) {
      this.response = extractedError.responseBody;
    }
    if (extractedError.responseText !== undefined) {
      this.body = extractedError.responseText;
    }
  }
}

/**
 * Wraps a fetch call with comprehensive error extraction.
 *
 * Usage:
 * ```typescript
 * const response = await fetch(url, options);
 * if (!response.ok) {
 *   throw await wrapApiError(response, url, options.method);
 * }
 * ```
 *
 * @param response - The fetch Response object (should be in error state)
 * @param url - The URL that was requested
 * @param method - The HTTP method used
 * @returns Promise that rejects with ApiError containing all error information
 */
export const wrapApiError = async (
  response: Response,
  url?: string,
  method?: string,
): Promise<never> => {
  const extractedError = await extractApiErrorFromResponse(
    response,
    url,
    method,
  );
  const customerMessage = createCustomerFacingMessage(extractedError);
  throw new ApiError(extractedError, customerMessage);
};

/**
 * Wraps a caught error with comprehensive error extraction.
 *
 * Usage:
 * ```typescript
 * try {
 *   const response = await fetch(url, options);
 *   if (!response.ok) {
 *     throw await wrapApiError(response, url, options.method);
 *   }
 * } catch (error) {
 *   throw await wrapCaughtError(error, response, url, options.method);
 * }
 * ```
 *
 * @param caughtError - The error that was caught
 * @param response - Optional Response object if available
 * @param url - Optional URL that was requested
 * @param method - Optional HTTP method used
 * @returns Promise that rejects with ApiError containing all error information
 */
export const wrapCaughtError = async (
  caughtError: unknown,
  response?: Response,
  url?: string,
  method?: string,
): Promise<never> => {
  const extractedError = await extractApiError(
    caughtError,
    response,
    url,
    method,
  );
  const customerMessage = createCustomerFacingMessage(extractedError);
  throw new ApiError(extractedError, customerMessage);
};

export { extractApiError, extractApiErrorFromResponse } from './extractApiError';
export type { ExtractedApiError } from './extractApiError';
export {
  getErrorResponseAttachment,
  hasHttpAttachments,
  isAttachedHttpMetadata,
  isRecord,
} from './httpErrorGuards';
export type {
  AttachedHttpMetadata,
  ErrorWithHttpAttachments,
} from './httpErrorGuards';
